import { useMemo, useRef, useState } from 'react';
import { CircleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useMatch, useNavigate } from 'react-router';
import { Popover, PopoverTrigger } from 'components/popovers/popover';
import { useAnalytics } from 'analytics';
import { ANALYTICS_EVENTS } from 'analytics/events';
import { usePaginatedResults } from 'hooks/usePaginatedResults';
import { ExportStatusValue } from 'types/result';
import { toIsoDate } from 'utils';
import { ResultCard } from 'widgets/home/ResultCard/ResultCard';
import {
  Heading,
  IndicatorButton,
  List,
  OverlayContent,
  Sentinel,
} from './FailedTransmissions.styles';

const REFETCH_INTERVAL_MS = 30000;
const LOOKBACK_DAYS = 7;

const startOfLookback = () => {
  const start = new Date();
  start.setDate(start.getDate() - LOOKBACK_DAYS);
  return toIsoDate(start);
};

export const FailedTransmissionsIndicator = () => {
  const { t } = useTranslation();
  const { trackEvent } = useAnalytics();
  const navigate = useNavigate();
  const isOnRecording = useMatch('/recording/:medatixxPatientId?') !== null;
  const [isOpen, setIsOpen] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const params = useMemo(
    () => ({ export_status: ExportStatusValue.Failed, start_date: startOfLookback() }),
    []
  );
  const { data, resultsState, handleStatusUpdate, hasNextPage, sentinelRef } = usePaginatedResults(
    params,
    { refetchInterval: REFETCH_INTERVAL_MS }
  );

  const count = data?.pages[0]?.count ?? 0;
  if (!count) return null;

  const label = t('topBar.failedTransmissions.heading', { count });

  return (
    <Popover
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (open) trackEvent(ANALYTICS_EVENTS.TITLEBAR_FAILED_TRANSMISSIONS_OPENED, { count });
      }}
    >
      <PopoverTrigger asChild>
        <IndicatorButton aria-label={label}>
          <CircleAlert className="h-4 w-4" aria-hidden="true" />
          <span aria-hidden="true">{count}</span>
        </IndicatorButton>
      </PopoverTrigger>
      <OverlayContent
        ref={overlayRef}
        align="end"
        aria-label={label}
        data-ph-no-autocapture
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          overlayRef.current?.focus();
        }}
      >
        <Heading>{label}</Heading>
        <List role="list" aria-label={label}>
          {resultsState.map((result, index) => (
            <ResultCard
              key={result.id}
              result={result}
              resultIndex={index}
              handleStatusUpdate={handleStatusUpdate}
              onOpen={() => {
                setIsOpen(false);
                void navigate(`/result/${result.id}`, { replace: isOnRecording });
              }}
            />
          ))}
          {hasNextPage && <Sentinel ref={sentinelRef} aria-hidden="true" />}
        </List>
      </OverlayContent>
    </Popover>
  );
};
